import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

export function PhotoGallery({ photos, selected, locked = [], onToggle }) {
    const [preview, setPreview] = useState();
    const [failedUrls, setFailedUrls] = useState(() => new Set());
    const imageFailed = url => setFailedUrls(current => new Set(current).add(url));
    const previewUrl = preview?.download_url || preview?.thumbnail_url;

    return <>
        <div className="work-photos">{photos.map(photo => {
            const thumbnailUrl = [photo.thumbnail_url, photo.download_url].find(url => url && !failedUrls.has(url));
            return <figure key={photo.id}>
                {thumbnailUrl ? <button type="button" className="work-photo-thumbnail"
                    aria-label={`View ${photo.title || 'issue photo'}`} onClick={() => setPreview(photo)}>
                    <img src={thumbnailUrl} alt={photo.title || 'Issue photo'} loading="lazy" width={128} height={112}
                        onError={() => imageFailed(thumbnailUrl)}/>
                </button> : <p>Photo unavailable</p>}
                <figcaption>{onToggle ? <label>
                    <input type="checkbox" checked={selected?.includes(photo.id) || locked.includes(photo.id)}
                        disabled={locked.includes(photo.id)} onChange={() => onToggle(photo.id)}/>
                    {photo.title || 'Photo'}
                </label> : photo.title || 'Photo'}</figcaption>
            </figure>;
        })}</div>
        {preview ? <Modal show onHide={() => setPreview(undefined)} size="lg" centered
            aria-label="Photo preview" onClick={event => event.stopPropagation()}>
            <Modal.Header closeButton><Modal.Title>{preview.title || 'Photo'}</Modal.Title></Modal.Header>
            <Modal.Body className="work-photo-preview">
                {previewUrl && !failedUrls.has(previewUrl) ? <img src={previewUrl}
                    alt={preview.title || 'Full-size issue photo'} onError={() => imageFailed(previewUrl)}/>
                    : <p role="alert">Photo unavailable. Close the preview and refresh the issues to try again.</p>}
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => setPreview(undefined)}>Close photo</Button></Modal.Footer>
        </Modal> : null}
    </>;
}
export function EvidenceUpload({ api, path, count, onPhoto, onBusy }) {
    const [queue, setQueue] = useState([]), [busy, setBusy] = useState(false), [error, setError] = useState('');
    const active = useRef(true), running = useRef(false), abort = useRef(null);
    useEffect(() => { active.current = true; return () => { active.current = false; abort.current?.abort(); }; }, []);
    async function upload(rows) {
        if (running.current)
            return;
        running.current = true;
        setBusy(true);
        onBusy?.(true);
        setError('');
        abort.current = new AbortController();
        try {
            for (const row of rows) {
                if (!active.current)
                    break;
                try {
                    if (!row.photo)
                        row.photo = await api(path, 'POST', { title: row.file.name, image_type: row.file.type === 'image/png' ? 'png' : row.file.type === 'image/webp' ? 'webp' : 'jpg' }, abort.current.signal);
                    if (!active.current)
                        break;
                    if (!row.photo.upload_url)
                        throw new Error('Upload link missing.');
                    const response = await fetch(row.photo.upload_url, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: row.file, signal: abort.current.signal });
                    if (!response.ok) {
                        if (response.status === 403)
                            row.photo = undefined;
                        throw new Error(`Photo upload failed (${response.status}).`);
                    }
                    row.done = true;
                    row.error = undefined;
                    if (active.current)
                        onPhoto(row.photo);
                }
                catch (e) {
                    row.error = e instanceof Error ? e.message : 'Upload failed.';
                }
                if (active.current)
                    setQueue(current => current.map(existing => existing.file === row.file ? { ...row } : existing));
            }
        }
        finally {
            running.current = false;
            if (active.current) {
                setBusy(false);
                onBusy?.(false);
            }
        }
    }
    function pick(files) {
        if (!files.length || running.current)
            return;
        if (files.length + count + queue.filter(row => !row.done).length > 20) {
            setError('Select at most 20 photos in total.');
            return;
        }
        if (files.some(file => file.size > 20 * 1024 * 1024 || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) {
            setError('Use JPEG, PNG or WebP photos, each 20 MB or smaller.');
            return;
        }
        const rows = files.map(file => ({ file }));
        setQueue(current => [...current, ...rows]);
        void upload(rows);
    }
    return _jsxs("div", { children: [_jsxs("label", { children: ["Add photos", _jsx("input", { type: "file", accept: "image/jpeg,image/png,image/webp", multiple: true, disabled: busy || count >= 20, onChange: event => { pick(Array.from(event.target.files || [])); event.target.value = ''; } })] }), busy ? _jsx("p", { role: "status", children: "Uploading photos\u2026" }) : null, error ? _jsx("p", { role: "alert", children: error }) : null, queue.filter(row => !row.done).map((row, index) => _jsxs("p", { children: [row.file.name, " \u00B7 ", row.error || 'Uploading…'] }, index)), queue.some(row => row.error && !row.done) ? _jsx("button", { type: "button", disabled: busy, onClick: () => void upload(queue.filter(row => !row.done)), children: "Retry failed photos" }) : null] });
}
export function ReportPhotos({ api, reportId, imageIds = [], resolveImageIds = [], children }) {
    const [photos, setPhotos] = useState([]), [error, setError] = useState(''), [revision, setRevision] = useState(0);
    const [loading, setLoading] = useState(true);
    const photoIdsKey = JSON.stringify([imageIds, resolveImageIds]);
    const hasPhotos = imageIds.length + resolveImageIds.length > 0;
    useEffect(() => {
        if (!hasPhotos) {
            setPhotos([]);
            setError('');
            setLoading(false);
            return;
        }
        const abort = new AbortController();
        setError('');
        setLoading(true);
        void api(`/reports/${reportId}/photos`, 'GET', undefined, abort.signal)
            .then(records => { if (!abort.signal.aborted) setPhotos(records); })
            .catch(e => { if (!abort.signal.aborted) setError(e.message); })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [api, reportId, hasPhotos, photoIdsKey, revision]);
    const byId = new Map(photos.map(photo => [photo.id, photo]));
    const group = ids => [...new Set(ids)].map(id => byId.get(id) || {id});

    return <>
        <div className="issue-photos" aria-label="Issue photos">
            <p><strong>Issue Photos:</strong></p>
            {loading ? <p role="status">Loading photos…</p> : null}
            {error ? <p role="alert">{error} <button type="button" onClick={() => setRevision(value => value + 1)}>Retry photos</button></p> : null}
            {!loading && !error ? (imageIds.length ? <PhotoGallery photos={group(imageIds)}/> : <p>No issue photos.</p>) : null}
        </div>
        {children}
        {resolveImageIds.length ? <div className="issue-photos" aria-label="Resolution photos">
            <p><strong>Resolution Photos:</strong></p>
            {!loading && !error ? <PhotoGallery photos={group(resolveImageIds)}/> : null}
        </div> : null}
    </>;
}
